import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { dayMs } from "../../../timeConstants.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    QuietHoursWindowFieldsWithDefaults,
    QuietHoursWindowSchemaTypes,
} from "../../types/quietHoursWindow.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "QuietHoursWindow" as const;

/** True when `timeZone` is a resolvable IANA identifier (e.g. "America/Vancouver"). */
function isValidTimeZone(timeZone: string): boolean {
    try {
        new Intl.DateTimeFormat(undefined, { timeZone });
        return true;
    } catch {
        return false;
    }
}

/**
 * A quiet-hours window: `startTimeMs` ms after local midnight in `timeZone`, lasting `durationMs`.
 * The window is evaluated against wall-clock time in `timeZone`, so it tracks the zone's DST. A
 * `durationMs` under a day keeps the window from covering the full cycle (which would never close).
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<QuietHoursWindow>>(...addSchemaKeyHook(SCHEMA_KEY))
export class QuietHoursWindow extends SchemaBase<QuietHoursWindow, QuietHoursWindowFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /** Ms after local midnight the window opens, in [0, dayMs). */
    @validate({ zodValidator: z.number().nonnegative().lt(dayMs) })
    @prop({ type: () => Number, required: true })
    public startTimeMs!: number;

    /** How long the window stays open; under a day so it always closes. */
    @validate({ zodValidator: z.number().nonnegative().lt(dayMs) })
    @prop({ type: () => Number, required: true })
    public durationMs!: number;

    /** IANA time zone the window's wall-clock times are read in. */
    @validate({ zodValidator: z.string().refine(isValidTimeZone, { message: "invalid IANA time zone" }) })
    @prop({ type: () => String, required: true })
    public timeZone!: string;

    static getters = {} as const satisfies Record<string, (self: QuietHoursWindow) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<QuietHoursWindowSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<QuietHoursWindow, QuietHoursWindowFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

QuietHoursWindow.dtoValidators = getZodSchemaFromClass(
    QuietHoursWindow,
) as unknown as DtoValidatorTypes<QuietHoursWindowSchemaTypes>;
